import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { Navbar, Footer } from '../components';
import { addCart } from '../redux/action';
import api from '../utils/api';
import toast from 'react-hot-toast';

const Wishlist = () => {
  const { isAuthenticated } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    fetchWishlist();
  }, [isAuthenticated, navigate]);

  const fetchWishlist = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/wishlists');
      setWishlist(res.data || []);
    } catch (err) {
      toast.error('Gagal memuat wishlist.');
    } finally {
      setLoading(false);
    }
  };

  const removeFromWishlist = async (productId, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await api.delete(`/api/wishlists/${productId}`);
      toast.success('Dihapus dari wishlist');
      setWishlist(wishlist.filter(item => item.product_id !== productId));
    } catch (err) {
      toast.error('Gagal menghapus dari wishlist');
    }
  };

  const formatRupiah = (angka) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(angka || 0);
  };

  const handleAddToCart = (product, e) => {
    e.preventDefault();
    dispatch(addCart(product));
    toast.success('Dimasukkan ke keranjang');
  };

  if (!isAuthenticated) return null;

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-zinc-950 text-white pb-20 pt-10 px-4 sm:px-6 lg:px-8 selection:bg-rose-500/30">
        <div className="max-w-7xl mx-auto">
          
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-extrabold tracking-tight">Wishlist Saya</h1>
              <p className="mt-2 text-sm text-zinc-400">Daftar produk favorit yang ingin Anda beli.</p>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="h-8 w-8 border-4 border-zinc-800 border-t-rose-500 rounded-full animate-spin"></div>
            </div>
          ) : wishlist.length === 0 ? (
            <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-12 text-center shadow-xl max-w-2xl mx-auto">
               <svg className="w-16 h-16 text-zinc-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
               </svg>
               <h3 className="text-xl font-bold text-white mb-2">Wishlist masih kosong</h3>
               <p className="text-zinc-400 mb-6">Anda belum menambahkan produk apa pun ke daftar favorit. Yuk jelajahi katalog kami!</p>
               <Link 
                 to="/product"
                 className="inline-flex items-center justify-center rounded-xl bg-white px-6 py-3 text-sm font-bold text-black shadow-sm hover:bg-zinc-200 transition"
               >
                 Jelajahi Produk
               </Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6">
              {wishlist.map((item) => {
                const product = item.product;
                if (!product) return null;
                
                return (
                  <Link key={item.id} to={`/product/${product.slug}`} className="group relative flex flex-col overflow-hidden rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-zinc-700 transition duration-300">
                    <div className="aspect-[4/3] w-full overflow-hidden bg-zinc-800">
                      <img
                        src={product.image_url || "/placeholder.png"}
                        alt={product.name}
                        className="h-full w-full object-cover object-center transition duration-500 group-hover:scale-105"
                        onError={(e) => { e.target.src = '/placeholder.png'; }}
                      />
                      <button
                        onClick={(e) => removeFromWishlist(product.id, e)}
                        className="absolute top-3 right-3 p-2 bg-black/50 backdrop-blur-md rounded-full text-rose-500 hover:bg-rose-500 hover:text-white transition"
                        title="Hapus dari Wishlist"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                    <div className="flex flex-1 flex-col p-4">
                      <p className="text-xs text-zinc-400 mb-1">{product.category_id}</p>
                      <h3 className="text-sm font-semibold text-white line-clamp-2">{product.name}</h3>
                      <div className="mt-auto pt-3 flex items-end justify-between">
                        <p className="text-base font-bold text-emerald-400">{formatRupiah(product.price)}</p>
                      </div>
                      <button 
                        onClick={(e) => handleAddToCart(product, e)}
                        className="mt-3 w-full py-2 bg-white/5 border border-white/10 rounded-xl text-xs font-bold text-white hover:bg-emerald-500 hover:border-emerald-500 transition"
                      >
                        + Keranjang
                      </button>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

        </div>
      </div>
      <Footer />
    </>
  );
};

export default Wishlist;
